import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Megaphone } from "lucide-react";

/**
 * Renders the three leaderboard ad slots for the landing page.
 * Empty slots show a friendly "Advertise Here" CTA linking to /advertise.
 */
export default function BannerAds() {
  const [slots, setSlots] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const keys = ["landing-top", "landing-middle", "landing-bottom"];
        const results = await Promise.all(
          keys.map((k) => api.get(`/ads/public/${k}`).then((r) => r.data).catch(() => null))
        );
        const map = {};
        keys.forEach((k, i) => { map[k] = results[i]; });
        setSlots(map);
      } catch { /* ignore */ }
    })();
  }, []);

  const renderSlot = (key) => {
    const s = slots[key];
    if (!s) return null;
    const meta = s.slot_meta || {};
    if (s.ad) {
      const src = s.ad.image_base64.startsWith("data:")
        ? s.ad.image_base64
        : `data:image/png;base64,${s.ad.image_base64}`;
      return (
        <a
          data-testid={`ad-${key}`}
          href={`${process.env.REACT_APP_BACKEND_URL}${s.ad.click_url}`}
          target="_blank"
          rel="sponsored noreferrer"
          className="block group"
        >
          <img
            src={src}
            alt={s.ad.advertiser_name || "Sponsored"}
            className="w-full h-[90px] md:h-[110px] object-cover rounded-lg border border-slate-800 group-hover:border-sky-400/40 transition"
          />
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mt-1.5 text-center">
            Sponsored · {s.ad.advertiser_name}
          </p>
        </a>
      );
    }
    return (
      <Link
        data-testid={`ad-${key}-empty`}
        to="/advertise"
        className="block border border-dashed border-slate-700 rounded-lg h-[90px] md:h-[110px] flex items-center justify-center group hover:border-sky-400/60 transition"
      >
        <div className="text-center">
          <Megaphone className="w-5 h-5 text-sky-400 mx-auto mb-1 group-hover:scale-110 transition" strokeWidth={1.5} />
          <p className="font-serif text-sm text-sky-100 group-hover:text-sky-300">Advertise Here</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
            {meta.label} · {meta.size} · ${meta.price_usd}
          </p>
        </div>
      </Link>
    );
  };

  return (
    <section data-testid="banner-ads" className="border-b border-slate-800">
      <div className="max-w-[1400px] mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Sponsored</p>
            <h2 className="font-serif text-2xl text-sky-100">Banner Advertising</h2>
          </div>
          <Link to="/advertise" className="text-xs px-3 py-1.5 rounded-md border border-sky-400/40 text-sky-300 hover:bg-sky-400 hover:text-slate-950 transition">
            Book a slot →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderSlot("landing-top")}
          {renderSlot("landing-middle")}
          {renderSlot("landing-bottom")}
        </div>
      </div>
    </section>
  );
}
